import { readCookie, setCookie } from './cookies';
import { isInternalBrowser } from './internal-traffic';
import { getLetterGrade, isTrackingAllowed, TRACKING_CONSENT_COOKIE } from '@privisy/domain';

/**
 * Extracts a clean domain name from a URL.
 */
export function extractDomain(url: string): string {
  try {
    const formattedUrl = url.trim().match(/^https?:\/\//i) ? url.trim() : `https://${url.trim()}`;
    const parsed = new URL(formattedUrl);
    return parsed.hostname.replace(/^www\./i, '');
  } catch {
    return url;
  }
}

// The score→letter ladder (issue #722) lives in @privisy/domain (#1692);
// re-exported so every existing `@privisy/ui-bridge` import keeps working.
export { getLetterGrade };


/**
 * Cryptographically hashes text using SHA-256 client-side.
 * Falls back to Node's crypto module in test/SSR environments where
 * Web Crypto is unavailable. The require('crypto') is inside the fallback
 * branch so bundlers (Vite, Next.js) tree-shake it from browser builds.
 */
async function sha256(text: string): Promise<string> {
  try {
    // If Web Crypto is available (browser or Node 18+ with globalThis.crypto.subtle)
    if (typeof globalThis?.crypto?.subtle?.digest === 'function') {
      const msgBuffer = new TextEncoder().encode(text.trim().toLowerCase());
      const hashBuffer = await globalThis.crypto.subtle.digest('SHA-256', msgBuffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    }

    // Fallback to Node's crypto (works in tests and SSR).
    // Dynamic require so bundlers don't pull Node crypto into browser builds.
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { createHash } = require('crypto') as typeof import('crypto');
    return createHash('sha256').update(text.trim().toLowerCase(), 'utf8').digest('hex');
  } catch (e) {
    console.warn('[Tracking] SHA-256 hashing failed:', e);
    return '';
  }
}

type GtagFn = (...args: unknown[]) => void;

/**
 * Returns a gtag callable: window.gtag when the Google tag snippet has defined
 * it, otherwise a stub that queues the command on window.dataLayer.
 *
 * The fallback is not cosmetic. www loaded both gtag.js and the inline stub
 * with next/script's `afterInteractive`, so window.gtag did not exist until
 * after hydration and every conversion fired before that point was dropped on
 * the floor — silently, since the old contract was "return null and no-op".
 * A queued command survives instead, and gtag.js replays it on load.
 *
 * `arguments` is load-bearing: gtag.js replays a queued command only when it
 * is an Arguments object. A plain `dataLayer.push(['event', ...])` is the GTM
 * idiom that plain gtag.js ignores — the same distinction the canonical
 * snippet relies on with `function gtag(){dataLayer.push(arguments)}`.
 *
 * Null only during SSR, where there is no window to queue on. When an ad
 * blocker stops gtag.js outright the queue simply never drains, which costs a
 * few array entries for the life of the page and nothing else — the
 * server-side leg is what covers that case.
 */
function getGtag(): GtagFn | null {
  if (typeof window === 'undefined') return null;
  const win = window as unknown as { gtag?: GtagFn; dataLayer?: unknown[] };
  if (typeof win.gtag === 'function') return win.gtag;
  win.dataLayer = win.dataLayer || [];
  return function queueGtagCommand(): void {
    // eslint-disable-next-line prefer-rest-params
    (window as unknown as { dataLayer: unknown[] }).dataLayer.push(arguments);
  };
}

/**
 * Fires a GA4 event via gtag.js. Raw dataLayer pushes are a GTM idiom that
 * plain gtag.js ignores — this must go through gtag('event', ...).
 */
function fireGtagEvent(name: string, params: Record<string, unknown>): void {
  if (!hasConsent()) return;
  const gtag = getGtag();
  if (gtag) gtag('event', name, params);
}

export const META_PIXEL_ID = '1026854133634612';

type FbqFn = (...args: unknown[]) => void;

/**
 * Returns window.fbq when available (loaded by the consent-gated Meta Pixel
 * loader in both apps). Null during SSR or when blocked by an ad blocker —
 * callers no-op silently.
 */
function getFbq(): FbqFn | null {
  if (typeof window !== 'undefined') {
    const win = window as unknown as { fbq?: FbqFn };
    if (typeof win.fbq === 'function') {
      return win.fbq;
    }
  }
  return null;
}

/**
 * Deterministic Meta dedup id shared with the server CAPI leg's event_id.
 * MUST stay in sync with signupEventId() in
 * packages/backend/src/integrations/meta-capi.ts.
 */
export function signupEventId(userId: string): string {
  return `signup_${userId}`;
}

/**
 * Deterministic Meta dedup id shared with the server CAPI leg's event_id.
 * MUST stay in sync with metaLeadEventId() in
 * packages/backend/src/integrations/meta-outbox.ts.
 */
export function metaLeadEventId(scanId: string): string {
  return `lead_${scanId}`;
}

/**
 * Deterministic Meta dedup id shared with the server CAPI leg's event_id.
 * MUST stay in sync with metaPurchaseEventId() in
 * packages/backend/src/integrations/meta-outbox.ts.
 */
export function metaPurchaseEventId(sessionId: string): string {
  return `purchase_${sessionId}`;
}

export interface MetaAttribution {
  fbp?: string;
  fbc?: string;
}

/**
 * Reads Meta browser attribution (_fbp and _fbc cookies).
 * Gated by hasConsent(): returns null when visitor has opted out or signals GPC.
 * If _fbc is missing but the URL carries fbclid, synthesizes fb.1.<ts>.<fbclid>.
 */
export function readMetaAttribution(): MetaAttribution | null {
  if (!hasConsent() || typeof window === 'undefined') return null;

  const fbp = readCookie('_fbp') ?? undefined;
  let fbc = readCookie('_fbc') ?? undefined;

  if (!fbc && window.location?.search) {
    try {
      const params = new URLSearchParams(window.location.search);
      const fbclid = params.get('fbclid');
      if (fbclid) {
        fbc = `fb.1.${Date.now()}.${fbclid}`;
      }
    } catch {
      // Fallback silently if URL search parsing fails
    }
  }

  if (!fbp && !fbc) return null;
  return { ...(fbp ? { fbp } : {}), ...(fbc ? { fbc } : {}) };
}

/**
 * Fires a Meta Pixel event via fbq. Consent-gated like every other tracker
 * call; no-ops when the pixel is absent (ad blocker / SSR).
 */
function fireMetaEvent(
  name: string,
  params: Record<string, unknown> = {},
  opts?: { eventID: string },
): void {
  if (!hasConsent()) return;
  const fbq = getFbq();
  if (!fbq) return;
  if (opts) fbq('track', name, params, opts);
  else fbq('track', name, params);
}

/**
 * Lead conversion for Meta: fires when an anonymous scan is accepted.
 */
export function trackMetaLead(scanId: string): void {
  fireMetaEvent('Lead', {}, { eventID: metaLeadEventId(scanId) });
}

/**
 * ViewContent conversion for Meta: fires when a locked report renders.
 * Deduped per reportId in sessionStorage.
 */
export function trackLockedReportViewed(reportId: string): void {
  try {
    if (typeof window !== 'undefined') {
      const key = `meta_view_content_${reportId}`;
      if (window.sessionStorage.getItem(key)) return;
      window.sessionStorage.setItem(key, 'true');
    }
  } catch {
    // Continue if sessionStorage throws
  }

  fireMetaEvent(
    'ViewContent',
    { content_type: 'product', content_ids: [reportId] },
    { eventID: `view_${reportId}` },
  );
}

/**
 * InitiateCheckout conversion for Meta: fires on unlock/monitoring checkout clicks.
 */
export function trackInitiateCheckout(params: {
  reportId?: string;
  plan: string;
  value?: number;
}): void {
  fireMetaEvent('InitiateCheckout', {
    content_type: 'product',
    content_ids: params.reportId ? [params.reportId] : [params.plan],
    ...(params.value !== undefined ? { value: params.value, currency: 'USD' } : {}),
  });
}


export const ADS_TAG_ID = 'AW-18052272147';

/**
 * Google Ads conversion action labels (Goals → Conversions in the Ads UI).
 * One label per funnel stage — never reuse a label across stages.
 */
export const ADS_CONVERSION_LABELS = {
  lead: '5isyCOKm8M0cEJOg_59D',
  signup: '_pi-CJqjkpkcEJOg_59D',
  purchase: 's2xxCJDMuJgcEJOg_59D',
} as const;

/**
 * Fires a Google Ads conversion for the given action label. Skipped in a
 * browser opted in as internal traffic: Ads ignores GA4's traffic_type, so
 * this is the only place a teammate's test conversion can be kept out.
 */
function fireAdsConversion(label: string, params: Record<string, unknown> = {}): void {
  if (!hasConsent() || isInternalBrowser()) return;
  const gtag = getGtag();
  if (gtag) {
    gtag('event', 'conversion', { send_to: `${ADS_TAG_ID}/${label}`, ...params });
  }
}

// The tracking-consent cookie name and the pure consent rule (#780) live in
// @privisy/domain (#1692); re-exported so www and the dashboard keep importing
// them from here. hasConsent() below is the client adapter over the rule.
export { TRACKING_CONSENT_COOKIE, isTrackingAllowed };

/**
 * Checks whether the user has opted out of tracking, either via the
 * privisy_consent cookie or via Global Privacy Control (GPC). The client-side
 * adapter over isTrackingAllowed() above.
 *
 * The cookie is matched anchored on a name boundary; a substring match would
 * let an unrelated cookie such as `foo_privisy_consent` flip the gate.
 */
export function hasConsent(): boolean {
  if (typeof window === 'undefined') return false;
  const gpcActive = typeof navigator !== 'undefined' &&
    Boolean((navigator as unknown as { globalPrivacyControl?: boolean }).globalPrivacyControl);
  const match = document.cookie.match(new RegExp('(^| )' + TRACKING_CONSENT_COOKIE + '=([^;]*)'));
  return isTrackingAllowed(match ? match[2] : null, gpcActive);
}

const CONSENT_TTL_SECONDS = 60 * 60 * 24 * 365; // choices persist 1 year

/**
 * Records an explicit consent choice. Written with the cross-subdomain cookie
 * semantics above — a host-only cookie here would silently kill every
 * conversion fired from the other app.
 */
export const setTrackingConsent = (granted: boolean) =>
  setCookie(TRACKING_CONSENT_COOKIE, String(granted), CONSENT_TTL_SECONDS);

/**
 * null = no explicit choice yet, which under the opt-out model means tracking
 * runs; true/false = an explicit choice made on /your-privacy-choices or via
 * the consent banner.
 */
export const getTrackingConsent = (): boolean | null => {
  const raw = readCookie(TRACKING_CONSENT_COOKIE);
  return raw === null ? null : raw === 'true';
};

/** Conversion value of one free scan, matching the Ads action's $1 setting. */
const LEAD_CONVERSION_VALUE = 1.0;

/**
 * Mint the id that ties the two legs of one lead conversion together.
 *
 * It has to be minted here rather than derived from the scanId, because the
 * lead fires *before* the scan request (see trackLeadSubmitted below) and the
 * scanId does not exist yet. The caller hands this id to the backend, which
 * reuses it verbatim as the ClickConversion orderId, so Google collapses the
 * client and server legs into the single conversion the action is configured
 * to count.
 */
function newLeadEventId(): string {
  const cryptoObj = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  const unique = cryptoObj?.randomUUID
    ? cryptoObj.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `lead_${unique}`;
}

/**
 * Lead conversion: user submitted the free scan / GPC checker form.
 * Fires on submit (before the scan request) — the intent is the lead,
 * regardless of whether the scan later succeeds.
 *
 * Returns the lead event id to hand to POST /api/lite-scan so the server-side
 * gclid leg can dedup against this one, or null when the visitor has opted out
 * (or is signalling GPC) and no conversion was fired. First-party Vercel
 * Analytics still records the submit either way — it is not an ad tracker.
 */
export function trackLeadSubmitted(url: string): string | null {
  const domain = extractDomain(url);
  safeVercelTrack('lead_submitted', { url, domain });
  if (!hasConsent()) return null;

  const leadEventId = newLeadEventId();
  fireAdsConversion(ADS_CONVERSION_LABELS.lead, {
    value: LEAD_CONVERSION_VALUE,
    currency: 'USD',
    transaction_id: leadEventId,
  });
  fireGtagEvent('generate_lead', {
    lead_type: 'free_scan',
    scan_domain: domain,
  });
  return leadEventId;
}

/**
 * Sign-up analytics: a new account was created. GA4 + Vercel only.
 *
 * The Google Ads sign-up *conversion* is NOT fired here — it is owned
 * server-side by the Clerk `user.created` webhook (see
 * packages/backend/src/api/routes/clerk-webhook.ts), which uploads an
 * Enhanced Conversion for Leads keyed on the hashed email. That path is
 * blocker-proof and exactly-once; this client event is best-effort analytics
 * that is fine to lose to an ad blocker. Both key on `signup_{userId}`, so
 * Google would collapse any overlap regardless.
 */
export async function trackSignup(userId: string, email?: string): Promise<void> {
  safeVercelTrack('sign_up', { user_id: userId });
  // user_data carries the hashed email to Google — consent-gated like every
  // other gtag call, not just the event pushes.
  if (email && hasConsent()) {
    const hashedEmail = await sha256(email);
    if (hashedEmail) {
      const gtag = getGtag();
      if (gtag) gtag('set', 'user_data', { sha256_email_address: hashedEmail });
    }
  }
  fireGtagEvent('sign_up', { method: 'clerk' });
  // Meta CompleteRegistration (best-effort browser leg; the authoritative
  // conversion is the server CAPI upload in the Clerk webhook). The eventID
  // matches the server event_id so Meta dedupes the pair.
  fireMetaEvent('CompleteRegistration', {}, { eventID: signupEventId(userId) });
}

/**
 * Returning-user sign-in. GA4 only — logins are not acquisitions and must
 * never send an Ads conversion.
 */
export function trackLogin(userId: string): void {
  safeVercelTrack('login', { user_id: userId });
  fireGtagEvent('login', { method: 'clerk' });
}

/**
 * Purchase conversion. Deduped per Stripe session via sessionStorage
 * (page reloads) and transaction_id (Google-side).
 */
export function trackPurchase(sessionId: string, value: number): void {
  try {
    if (typeof window !== 'undefined') {
      const key = `purchase_tracked_${sessionId}`;
      if (window.sessionStorage.getItem(key)) return;
      window.sessionStorage.setItem(key, 'true');
    }
  } catch {
    // Accessing sessionStorage throws when storage is blocked entirely —
    // still fire the conversion and rely on transaction_id for dedupe.
  }
  safeVercelTrack('purchase', { session_id: sessionId, value });
  fireAdsConversion(ADS_CONVERSION_LABELS.purchase, {
    value,
    currency: 'USD',
    transaction_id: sessionId,
  });
  fireGtagEvent('purchase', {
    value,
    currency: 'USD',
    transaction_id: sessionId,
  });
  fireMetaEvent(
    'Purchase',
    { value, currency: 'USD', content_type: 'product' },
    { eventID: metaPurchaseEventId(sessionId) },
  );
}

/**
 * Safe wrapper for Vercel Analytics tracking using window.va.
 */
function safeVercelTrack(event: string, params: Record<string, unknown>): void {
  if (typeof window !== 'undefined') {
    type VaTrackFn = (cmd: string, eventName: string, eventParams: Record<string, unknown>) => void;
    type VaFnWithQueue = VaTrackFn & { q?: unknown[][] };
    const win = window as unknown as { va?: VaFnWithQueue };
    const vaFn: VaFnWithQueue = win.va || (function vaQueue(...args: unknown[]) {
      const currentWin = window as unknown as { va?: VaFnWithQueue };
      if (currentWin.va) {
        currentWin.va.q = currentWin.va.q || [];
        currentWin.va.q.push(args);
      }
    } as VaFnWithQueue);
    win.va = vaFn;
    win.va('track', event, params);
  }
}

/**
 * Tracks the initiation of a website scan.
 */
export function trackScanStarted(url: string): void {
  const domain = extractDomain(url);

  // Vercel Analytics
  safeVercelTrack('scan_started', { url, domain });

  // Google Analytics via gtag
  fireGtagEvent('search', {
    search_term: domain,
    scan_type: 'site_audit',
  });

  fireGtagEvent('scan_started', {
    scan_url: url,
    scan_domain: domain,
  });
}

/**
 * Tracks the successful completion of a website scan.
 */
export async function trackScanComplete(
  url: string,
  result: {
    score: number;
    sections?: {
      trackerDetection?: {
        details?: {
          summary?: {
            totalThirdParty?: number;
          };
        };
      };
      uiCompliance?: {
        checks?: Array<{ status: string }>;
      };
    };
  },
  durationMs: number
): Promise<void> {
  const domain = extractDomain(url);
  const score = result.score;
  const tier = getLetterGrade(score);

  // Safely extract trackers
  const trackersFound = result.sections?.trackerDetection?.details?.summary?.totalThirdParty ?? 0;

  // Safely extract failed UI checks count
  const uiChecks = result.sections?.uiCompliance?.checks ?? [];
  const uiChecksFailed = uiChecks.filter((c: { status: string }) => c.status === 'failed').length;

  const metadata = {
    url,
    domain,
    score,
    tier,
    duration_ms: durationMs,
    trackers_found: trackersFound,
    ui_checks_failed: uiChecksFailed,
  };

  // Vercel Analytics
  safeVercelTrack('scan_complete', metadata);

  // Google Analytics via gtag (the lead conversion fires at form submit, not here)
  fireGtagEvent('scan_complete', {
    scan_url: url,
    scan_domain: domain,
    scan_score: score,
    scan_tier: tier,
    scan_duration_ms: durationMs,
    trackers_found: trackersFound,
    ui_checks_failed: uiChecksFailed,
  });
}

/**
 * Tracks scan failures and timeouts.
 */
export function trackScanError(url: string, errorMessage: string, stage: string): void {
  const domain = extractDomain(url);

  // Vercel Analytics
  safeVercelTrack('scan_error', {
    url,
    domain,
    error_message: errorMessage,
    stage,
  });

  // Google Analytics via gtag
  fireGtagEvent('scan_error', {
    scan_url: url,
    scan_domain: domain,
    error_message: errorMessage,
    stage,
  });
}

/**
 * Tracks a custom event to both Vercel Analytics and GA4 (via gtag).
 */
export function trackCustomEvent(event: string, params?: Record<string, unknown>): void {
  safeVercelTrack(event, params || {});
  fireGtagEvent(event, params || {});
}
